import { EventModel } from "@nova/db-table-live-video"
import { DroneIconColors, droneIconColors } from "features/map/layers/video/VideoDroneLayer"
import { TypedEventEmitter } from "@nova/typed-event-emitter"
import { UnsubscribeListener } from "@reduxjs/toolkit"

export type VideoControllerEvents =
    | "dataChanged"
    | "videoTrackChanged"
    | "videoProgressChanged"
    | "isPlayingChanged"
    | "videoResolutionIndexChanged"
    | "videoAvailabilityChanged"
    | "connectivityChanged"
    | "audioAvailabilityChanged"
    | "audioSettingsChanged"

const VOLUME_PERCENT_ON_UNMUTE = 100

export type VideoControllerData = Extract<EventModel, { type: "liveVideo" }> & {
    fov?: number | number[]
}

export abstract class AbstractVideoController extends TypedEventEmitter<VideoControllerEvents> {
    protected flightId: string
    protected stopListeningToFlightChanges?: UnsubscribeListener
    protected droneIconColor: DroneIconColors = droneIconColors[0]
    protected data: VideoControllerData[] = []
    protected hasFetched = false
    protected videoProgress = 0
    protected videoElement: HTMLVideoElement | null = null
    protected isPlaying = false
    protected hasPlayed = false
    // Ephemeral: broadcaster has temporarily stopped publishing during a live stream.
    protected isDisconnected = false
    // Broadcaster stopped the camera but is still publishing audio.
    protected cameraIsOff = false
    protected audioIsAvailable = false
    protected isMuted = true
    protected volumePercent = VOLUME_PERCENT_ON_UNMUTE
    constructor({ flightId }: { flightId: string }) {
        super()
        this.flightId = flightId
    }
    abstract initialize(signal: AbortSignal): Promise<void>
    abstract deinitialize(): Promise<void>
    abstract fetchAllData(zoomTo?: true): Promise<void>
    abstract attachToElement(element: HTMLVideoElement): () => void

    pushData(data: VideoControllerData) {
        const lastDataItem = this.data.at(-1)
        if (!lastDataItem || (data.createdAt || 0) > (lastDataItem.createdAt || 0)) {
            this.data.push(data)
        } else {
            this.data = [...this.data, data].toSorted((a, b) => a.timestamp - b.timestamp)
        }
        this.emit("dataChanged")
    }
    getData() {
        return this.data
    }
    getCurrentFrameNumber() {
        // Minimum: 0, maximum: this.data.length, in general: this.videoProgress * this.data.length
        return Math.max(
            Math.min(Math.round(this.videoProgress * this.data.length), this.data.length),
            0
        )
    }
    getCurrentFrameData() {
        const currentFrame = this.data[this.getCurrentFrameNumber() - 1]
        return currentFrame
    }
    getAllCoords() {
        return this.data.map((d) => ({
            lat: d.latitude,
            lng: d.longitude,
        }))
    }
    getDroneIconColor() {
        return this.droneIconColor
    }
    getVideoElement() {
        return this.videoElement
    }
    isPlayingVideo() {
        return this.isPlaying
    }
    hasPlayedVideo() {
        return this.hasPlayed
    }
    isStreamDisconnected() {
        return this.isDisconnected
    }
    isCameraOff() {
        return this.cameraIsOff
    }
    protected setStreamAvailability({
        cameraIsOff,
        isDisconnected,
    }: {
        cameraIsOff: boolean
        isDisconnected: boolean
    }) {
        if (cameraIsOff === this.cameraIsOff && isDisconnected === this.isDisconnected) return
        this.cameraIsOff = cameraIsOff
        this.isDisconnected = isDisconnected
        this.emit("connectivityChanged")
    }
    hasFetchedOriginalData() {
        return this.hasFetched
    }
    hasAudio() {
        return this.audioIsAvailable
    }
    isAudioMuted() {
        return this.isMuted
    }
    getVolumePercent() {
        return this.volumePercent
    }
    setAudioMuted(muted: boolean) {
        if (this.isMuted === muted) return
        this.isMuted = muted
        if (!muted && this.volumePercent === 0) {
            this.volumePercent = VOLUME_PERCENT_ON_UNMUTE
        }
        this.applyAudioSettings()
        this.emit("audioSettingsChanged")
    }
    setVolumePercent(volumePercent: number) {
        const clamped = Math.max(0, Math.min(100, Math.round(volumePercent)))
        if (clamped === this.volumePercent && this.isMuted === (clamped === 0)) return
        this.volumePercent = clamped
        this.isMuted = clamped === 0
        this.applyAudioSettings()
        this.emit("audioSettingsChanged")
    }
    protected applyAudioSettings() {
        if (!this.videoElement) return
        this.videoElement.muted = this.isMuted
        this.videoElement.volume = this.volumePercent / 100
    }
}
